/**
 * Action Tracker Component
 * Allows staff to track member actions that contribute to incentives
 * Trust Marketplace - Incentive Tracking System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  Edit,
  Eye,
  Filter,
  Download
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface ActionTrackerProps {
  memberId?: number;
  programId?: number;
}

interface MemberAction {
  id: number;
  action_number: string;
  member_id: number;
  action_type: string;
  action_name: string;
  action_description: string;
  status: string;
  initiated_at: string;
  completed_at: string | null;
  verified_at: string | null;
  outcome_met: boolean;
  incentive_points: number;
  incentive_amount: number;
  created_at: string;
}

const ACTION_TYPES = [
  { value: 'ENROLLMENT', label: 'Enrollment' },
  { value: 'PREVENTIVE_CARE', label: 'Preventive Care' },
  { value: 'HEALTH_RISK_ASSESSMENT', label: 'Health Risk Assessment' },
  { value: 'SERVICE_COMPLETION', label: 'Service Completion' },
  { value: 'APPOINTMENT_ATTENDANCE', label: 'Appointment Attendance' },
  { value: 'MEDICATION_ADHERENCE', label: 'Medication Adherence' },
  { value: 'WELLNESS_ACTIVITY', label: 'Wellness Activity' },
  { value: 'DOCUMENT_UPLOAD', label: 'Document Upload' },
  { value: 'SURVEY_COMPLETION', label: 'Survey Completion' },
  { value: 'PROGRAM_MILESTONE', label: 'Program Milestone' },
  { value: 'CUSTOM', label: 'Custom' }
];

const ACTION_STATUSES = [
  { value: 'INITIATED', label: 'Initiated', color: 'bg-gray-100 text-gray-800' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'VERIFIED', label: 'Verified', color: 'bg-purple-100 text-purple-800' },
  { value: 'FAILED', label: 'Failed', color: 'bg-red-100 text-red-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-orange-100 text-orange-800' }
];

const ActionTracker: React.FC<ActionTrackerProps> = ({ memberId, programId }) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [actions, setActions] = useState<MemberAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<MemberAction | null>(null);

  // Form state for creating new action
  const [newAction, setNewAction] = useState({
    member_id: memberId || 0,
    program_id: programId || 0,
    action_type: '',
    action_name: '',
    action_description: '',
    outcome_value: '',
    outcome_target: '',
    outcome_unit: ''
  });

  useEffect(() => {
    console.log('🎯 ActionTracker mounted', { memberId, programId, filterStatus, filterType });
    fetchActions();
  }, [memberId, programId, filterStatus, filterType]);

  const fetchActions = async () => {
    try {
      console.log('📡 ActionTracker: Fetching actions...');
      setLoading(true);
      const params = new URLSearchParams();
      if (memberId) params.append('member_id', memberId.toString());
      if (programId) params.append('program_id', programId.toString());
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('action_type', filterType);

      const url = getWeaveinApiUrl(`/api/incentives/actions?${params.toString()}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch actions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setActions(data);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createAction = async () => {
    try {
      const response = await authFetch(getWeaveinApiUrl('/api/incentives/actions'), {
        method: 'POST',
        jsonBody: {
          ...newAction,
          outcome_value: newAction.outcome_value ? parseFloat(newAction.outcome_value) : null,
          outcome_target: newAction.outcome_target ? parseFloat(newAction.outcome_target) : null
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create action');
      }

      // Reset form and close modal
      setNewAction({
        member_id: memberId || 0,
        program_id: programId || 0,
        action_type: '',
        action_name: '',
        action_description: '',
        outcome_value: '',
        outcome_target: '',
        outcome_unit: ''
      });
      setIsCreateModalOpen(false);
      fetchActions();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create action');
    }
  };

  const updateActionStatus = async (actionId: number, status: string) => {
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/incentives/actions/${actionId}`), {
        method: 'PUT',
        jsonBody: { status }
      });

      if (!response.ok) {
        throw new Error('Failed to update action');
      }

      fetchActions();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update action');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = ACTION_STATUSES.find(s => s.value === status);
    return (
      <Badge className={statusConfig?.color || 'bg-gray-100 text-gray-800'}>
        {statusConfig?.label || status}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'VERIFIED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'IN_PROGRESS':
      case 'INITIATED':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'FAILED':
      case 'CANCELLED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  if (loading && actions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-2xl font-bold text-gray-900">{t('incentives.action_tracker', 'Action Tracker')}</h2>
          )}
          <p className="text-sm text-gray-600 mt-1">
            {t('incentives.track_member_actions_and_incentive', 'Track member actions and incentive eligibility')}
          </p>
        </div>

        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-v2accent hover:bg-v2accent/90">
              <Plus className="w-4 h-4 mr-2" />
              {t('incentives.track_new_action', 'Track New Action')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('incentives.track_new_member_action', 'Track New Member Action')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="action_type">{t('incentives.action_type', 'Action Type *')}</Label>
                  <Select
                    value={newAction.action_type}
                    onValueChange={(value) => setNewAction({ ...newAction, action_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('incentives.select_action_type', 'Select action type')} />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="action_name">{t('incentives.action_name', 'Action Name *')}</Label>
                  <Input
                    id="action_name"
                    value={newAction.action_name}
                    onChange={(e) => setNewAction({ ...newAction, action_name: e.target.value })}
                    placeholder="e.g., Annual Physical Exam"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="action_description">{t('incentives.description', 'Description')}</Label>
                <Textarea
                  id="action_description"
                  value={newAction.action_description}
                  onChange={(e) => setNewAction({ ...newAction, action_description: e.target.value })}
                  placeholder={t('incentives.describe_the_action', 'Describe the action...')}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="outcome_value">{t('incentives.outcome_value', 'Outcome Value')}</Label>
                  <Input
                    id="outcome_value"
                    type="number"
                    step="0.01"
                    value={newAction.outcome_value}
                    onChange={(e) => setNewAction({ ...newAction, outcome_value: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="outcome_target">{t('incentives.target_value', 'Target Value')}</Label>
                  <Input
                    id="outcome_target"
                    type="number"
                    step="0.01"
                    value={newAction.outcome_target}
                    onChange={(e) => setNewAction({ ...newAction, outcome_target: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="outcome_unit">{t('incentives.unit', 'Unit')}</Label>
                  <Input
                    id="outcome_unit"
                    value={newAction.outcome_unit}
                    onChange={(e) => setNewAction({ ...newAction, outcome_unit: e.target.value })}
                    placeholder="e.g., lbs, mg/dL"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  {t('incentives.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={createAction}
                  disabled={!newAction.action_type || !newAction.action_name}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {t('incentives.track_action', 'Track Action')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="filter_status">{t('incentives.filter_by_status', 'Filter by Status')}</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('incentives.all_statuses_1', 'All Statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('incentives.all_statuses', 'All Statuses')}</SelectItem>
                {ACTION_STATUSES.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_type">{t('incentives.filter_by_type', 'Filter by Type')}</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder={t('incentives.all_types_1', 'All Types')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('incentives.all_types', 'All Types')}</SelectItem>
                {ACTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={fetchActions}
              className="w-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('incentives.apply_filters', 'Apply Filters')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Actions List */}
      {error ? (
        <Card className="p-6 bg-red-50 border-red-200">
          <p className="text-red-800">Error: {error}</p>
        </Card>
      ) : actions.length === 0 ? (
        <Card className="p-12 text-center">
          <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('incentives.no_actions_found', 'No actions found')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('incentives.start_tracking_member_actions_to', 'Start tracking member actions to earn incentives')}
          </p>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-v2accent hover:bg-v2accent/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('incentives.track_your_first_action', 'Track Your First Action')}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <Card key={action.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {getStatusIcon(action.status)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {action.action_name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {action.action_number} • {formatDate(action.initiated_at)}
                      </p>
                    </div>
                    {getStatusBadge(action.status)}
                  </div>

                  {action.action_description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {action.action_description}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">{t('incentives.type', 'Type:')}</span>
                      <Badge variant="outline" className="text-xs">
                        {ACTION_TYPES.find(at => at.value === action.action_type)?.label || action.action_type}
                      </Badge>
                    </div>

                    {action.incentive_points > 0 && (
                      <div className="flex items-center gap-1 text-purple-600">
                        <span className="font-medium">{action.incentive_points}</span>
                        <span className="text-xs">points</span>
                      </div>
                    )}

                    {action.incentive_amount > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <span className="font-medium">{formatCurrency(action.incentive_amount)}</span>
                      </div>
                    )}

                    {action.completed_at && (
                      <div className="text-xs text-gray-500">
                        Completed: {formatDate(action.completed_at)}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {action.status !== 'COMPLETED' && action.status !== 'VERIFIED' && (
                    <div className="flex gap-2 mt-3">
                      {action.status === 'INITIATED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateActionStatus(action.id, 'IN_PROGRESS')}
                        >
                          {t('incentives.mark_in_progress', 'Mark In Progress')}
                        </Button>
                      )}
                      {(action.status === 'INITIATED' || action.status === 'IN_PROGRESS') && (
                        <Button
                          size="sm"
                          className="bg-v2accent hover:bg-v2accent/90"
                          onClick={() => updateActionStatus(action.id, 'COMPLETED')}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t('incentives.mark_complete', 'Mark Complete')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Summary Footer */}
      {actions.length > 0 && (
        <Card className="p-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Showing {actions.length} action{actions.length !== 1 ? 's' : ''}
            </span>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              {t('incentives.export', 'Export')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ActionTracker;
